import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, 
  Plus, 
  Activity, 
  Shield, 
  AlertCircle,
  TrendingUp,
  Clock,
  Users
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getUserWebIDs } from '../../services/webids';
import { WebIDData } from '../../services/webids';
import { Button } from '../ui';
import WebIDCard from './WebIDCard';
import OnboardingNotification from '../notifications/OnboardingNotification';

/**
 * @component PanelDashboard
 * @description Dashboard principal del panel de usuario
 */

interface DashboardStats {
  totalWebIds: number;
  activeWebIds: number;
  totalScans: number;
  recentScans: number;
}

const PanelDashboard: React.FC = () => {
  const { user } = useAuth();
  const [webIds, setWebIds] = useState<WebIDData[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalWebIds: 0,
    activeWebIds: 0,
    totalScans: 0,
    recentScans: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const userWebIds = await getUserWebIDs(user!.uid);
      setWebIds(userWebIds);

      // Calcular estadísticas
      const totalScans = userWebIds.reduce((sum, webId) => sum + webId.scanCount, 0);
      const activeWebIds = userWebIds.filter(webId => webId.status === 'active').length;
      
      // Escaneos recientes (últimos 7 días)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const recentScans = userWebIds.filter(webId => 
        webId.lastScanned && webId.lastScanned.toDate() > sevenDaysAgo
      ).length;

      setStats({
        totalWebIds: userWebIds.length,
        activeWebIds,
        totalScans,
        recentScans,
      });

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Error al cargar información del dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Dashboard
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Bienvenido, {user?.displayName || user?.email}
          </p>
        </div>

        <Link
          to="/activar"
          className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base min-h-[44px]"
        >
          <Plus className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Vincular nueva pulsera</span>
          <span className="sm:hidden">Nueva pulsera</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Onboarding Notification */}
      <OnboardingNotification webIds={webIds} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Pulseras</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalWebIds}</p>
            </div>
            <div className="p-2 sm:p-3 bg-primary-100 rounded-full">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Pulseras Activas</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.activeWebIds}</p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 rounded-full">
              <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Escaneos</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.totalScans}</p>
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 rounded-full">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600">Escaneos Recientes</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.recentScans}</p>
            </div>
            <div className="p-2 sm:p-3 bg-purple-100 rounded-full">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* WebIDs Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Mis Pulseras NFC
            </h2>
            <Link
              to="/panel/webids"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium min-h-[44px] flex items-center"
            >
              Ver todas
            </Link>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {webIds.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <CreditCard className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                No tienes pulseras NFC
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 px-4">
                Vincula tu primera pulsera para comenzar a usar el sistema de emergencias.
              </p>
              <Link
                to="/activar"
                className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px] text-sm sm:text-base"
              >
                <Plus className="h-4 w-4 mr-2" />
                Vincular primera pulsera
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {webIds.slice(0, 6).map((webId) => (
                <WebIDCard
                  key={webId.id}
                  webId={webId}
                  onUpdate={loadDashboardData}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Acciones Rápidas
          </h2>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/activar"
              className="flex items-center justify-center p-4 h-auto border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Plus className="h-5 w-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Vincular Pulsera</div>
                <div className="text-sm text-gray-500">Activar nueva pulsera NFC</div>
              </div>
            </Link>

            <Link
              to="/panel/webids"
              className="flex items-center justify-center p-4 h-auto border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Activity className="h-5 w-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Ver Actividad</div>
                <div className="text-sm text-gray-500">Historial de escaneos</div>
              </div>
            </Link>

            <Link
              to="/dashboard/settings"
              className="flex items-center justify-center p-4 h-auto border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Users className="h-5 w-5 mr-3" />
              <div className="text-left">
                <div className="font-medium">Configuración</div>
                <div className="text-sm text-gray-500">Ajustes de cuenta</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelDashboard;
